import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Upload, FileZip, Check, Warning, Spinner, House, ClockCounterClockwise, ArrowsClockwise, TreeStructure, CloudArrowDown } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { XmlTreeView } from '../components/XmlTreeView';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ImportPage() {
    const [file, setFile] = useState(null);
    const [brokerId, setBrokerId] = useState('none');
    const [brokers, setBrokers] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [importProgress, setImportProgress] = useState(null);
    const [result, setResult] = useState(null);
    const [importLogs, setImportLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(true);
    const [analyzing, setAnalyzing] = useState(false);
    const [analysisResult, setAnalysisResult] = useState(null);
    const [ftpFiles, setFtpFiles] = useState(null);
    const [ftpLoading, setFtpLoading] = useState(false);
    const [ftpAnalyzing, setFtpAnalyzing] = useState(false);
    const [loadedAnalyses, setLoadedAnalyses] = useState([]);
    const [activeAnalysisIdx, setActiveAnalysisIdx] = useState(0);

    const fetchLogs = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/api/import/logs`, { withCredentials: true });
            setImportLogs(data);
        } catch (error) {
            console.error('Error fetching import logs:', error);
        } finally {
            setLogsLoading(false);
        }
    }, []);

    useEffect(() => {
        const fetchBrokers = async () => {
            try {
                const { data } = await axios.get(`${API}/api/brokers`, { withCredentials: true });
                setBrokers(data);
            } catch (error) {
                console.error('Error fetching brokers:', error);
            }
        };
        fetchBrokers();
        fetchLogs();
    }, [fetchLogs]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            if (!selectedFile.name.toLowerCase().endsWith('.zip')) {
                toast.error('Bitte wählen Sie eine ZIP-Datei aus');
                return;
            }
            setFile(selectedFile);
            setResult(null);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const droppedFile = e.dataTransfer.files?.[0];
        if (droppedFile) {
            if (!droppedFile.name.toLowerCase().endsWith('.zip')) {
                toast.error('Bitte wählen Sie eine ZIP-Datei aus');
                return;
            }
            setFile(droppedFile);
            setResult(null);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleUpload = async () => {
        if (!file) {
            toast.error('Bitte wählen Sie eine Datei aus');
            return;
        }

        setUploading(true);
        setProgress(0);
        setResult(null);
        setImportProgress(null);

        const formData = new FormData();
        formData.append('file', file);
        if (brokerId && brokerId !== 'none') {
            formData.append('broker_id', brokerId);
        }

        try {
            const { data } = await axios.post(`${API}/api/import/openimmo`, formData, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (data.started) {
                toast.success(`${data.total} Immobilie(n) werden verarbeitet...`);
                pollImportProgress();
            } else {
                // Legacy fallback
                setProgress(100);
                setResult(data);
                setUploading(false);
                fetchLogs();
            }
        } catch (error) {
            const errorMessage = error.response?.data?.detail || 'Fehler beim Import';
            toast.error(errorMessage);
            setResult({ error: errorMessage });
            setUploading(false);
        }
    };

    const pollImportProgress = () => {
        const interval = setInterval(async () => {
            try {
                const { data } = await axios.get(`${API}/api/import/progress`, { withCredentials: true });
                setImportProgress(data);
                if (data.total > 0) {
                    setProgress(Math.round((data.current_index / data.total) * 100));
                }
                if (data.status === 'completed' || data.status === 'error' || data.status === 'idle') {
                    clearInterval(interval);
                    setUploading(false);
                    setProgress(100);
                    if (data.status === 'completed') {
                        setResult({
                            created: data.created,
                            updated: data.updated,
                            total: data.total,
                            errors: data.errors
                        });
                        toast.success(data.message || 'Import abgeschlossen');
                    } else if (data.status === 'error') {
                        setResult({ error: data.message || 'Import fehlgeschlagen' });
                        toast.error(data.message);
                    }
                    fetchLogs();
                }
            } catch (error) {
                clearInterval(interval);
                setUploading(false);
            }
        }, 1500);
    };

    const handleAnalyze = async () => {
        if (!file) {
            toast.error('Bitte wählen Sie eine Datei aus');
            return;
        }
        setAnalyzing(true);
        setAnalysisResult(null);
        const formData = new FormData();
        formData.append('file', file);
        try {
            const { data } = await axios.post(`${API}/api/import/analyze`, formData, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setAnalysisResult(data);
            toast.success(`${data.properties.length} Immobilie(n) in der XML gefunden`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Analyse fehlgeschlagen');
        }
        setAnalyzing(false);
    };

    const handleLoadFtpFiles = async () => {
        setFtpLoading(true);
        try {
            const { data } = await axios.get(`${API}/api/ftp/list-files`, { withCredentials: true });
            setFtpFiles(data.files);
            if (data.files.length === 0) {
                toast.info('Keine ZIP-Dateien auf dem FTP-Server gefunden');
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'FTP-Verbindung fehlgeschlagen');
        }
        setFtpLoading(false);
    };

    const handleAnalyzeFtpFile = async (filename, folder) => {
        setFtpAnalyzing(true);
        try {
            const { data } = await axios.post(`${API}/api/ftp/analyze-file`, { filename, folder }, { withCredentials: true });
            setLoadedAnalyses(prev => {
                const existing = prev.findIndex(a => a.filename === filename);
                if (existing >= 0) {
                    setActiveAnalysisIdx(existing);
                    return prev;
                }
                const updated = [...prev, data];
                setActiveAnalysisIdx(updated.length - 1);
                return updated;
            });
            setAnalysisResult(data);
            toast.success(`${data.properties.length} Immobilie(n) in ${filename} gefunden`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Analyse fehlgeschlagen');
        }
        setFtpAnalyzing(false);
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatTimestamp = (ts) => {
        if (!ts) return '-';
        const d = new Date(ts);
        return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
            + ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="space-y-6" data-testid="import-page">
            <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-cabinet)' }}>
                    OpenImmo Import
                </h1>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Upload Card */}
                <Card className="border" data-testid="upload-card">
                    <CardHeader>
                        <CardTitle style={{ fontFamily: 'var(--font-cabinet)' }}>
                            ZIP-Datei hochladen
                        </CardTitle>
                        <CardDescription>
                            Laden Sie eine OpenImmo 1.2.7 ZIP-Datei hoch, um Immobilien zu importieren
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Broker Selection */}
                        <div className="space-y-2">
                            <Label>Makler zuweisen (optional)</Label>
                            <Select value={brokerId} onValueChange={setBrokerId}>
                                <SelectTrigger className="rounded-sm" data-testid="broker-select">
                                    <SelectValue placeholder="Makler auswählen..." />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Kein Makler</SelectItem>
                                    {brokers.map((broker) => (
                                        <SelectItem key={broker.id} value={broker.id}>
                                            {broker.name} {broker.company ? `(${broker.company})` : ''}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Drop Zone */}
                        <div
                            className={`border-2 border-dashed rounded-sm p-8 text-center transition-colors ${
                                file ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                            }`}
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            data-testid="drop-zone"
                        >
                            <input
                                type="file"
                                accept=".zip"
                                onChange={handleFileChange}
                                className="hidden"
                                id="file-upload"
                                data-testid="file-input"
                            />
                            <label htmlFor="file-upload" className="cursor-pointer">
                                {file ? (
                                    <div className="space-y-2">
                                        <FileZip size={48} className="mx-auto text-primary" />
                                        <p className="font-medium">{file.name}</p>
                                        <p className="text-sm text-muted-foreground">
                                            {formatFileSize(file.size)}
                                        </p>
                                        <Button 
                                            type="button" 
                                            variant="outline" 
                                            size="sm"
                                            className="rounded-sm"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setFile(null);
                                            }}
                                        >
                                            Andere Datei wählen
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        <Upload size={48} className="mx-auto text-muted-foreground" />
                                        <p className="font-medium">ZIP-Datei hierher ziehen</p>
                                        <p className="text-sm text-muted-foreground">
                                            oder klicken zum Auswählen
                                        </p>
                                    </div>
                                )}
                            </label>
                        </div>

                        {/* Progress */}
                        {uploading && (
                            <div className="space-y-3" data-testid="upload-progress">
                                {importProgress && importProgress.status === 'running' ? (
                                    <>
                                        <div className="flex items-center gap-2 text-sm font-medium">
                                            <Spinner className="animate-spin" size={14} />
                                            <span>{importProgress.message}</span>
                                        </div>
                                        <Progress value={progress} className="h-2" />
                                        <div className="flex justify-between text-xs text-muted-foreground">
                                            <span>{importProgress.current_index} von {importProgress.total}</span>
                                            <div className="flex gap-3">
                                                <span className="text-green-600">{importProgress.created} neu</span>
                                                <span className="text-blue-600">{importProgress.updated} aktualisiert</span>
                                                {importProgress.errors?.length > 0 && (
                                                    <span className="text-red-600">{importProgress.errors.length} Fehler</span>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex items-center justify-between text-sm">
                                            <span>Datei wird hochgeladen...</span>
                                        </div>
                                        <Progress value={30} className="h-2" />
                                    </>
                                )}
                            </div>
                        )}

                        {/* Upload Button */}
                        <div className="grid grid-cols-2 gap-3">
                            <Button 
                                onClick={handleUpload} 
                                disabled={!file || uploading || analyzing}
                                className="rounded-sm"
                                data-testid="upload-btn"
                            >
                                {uploading ? (
                                    <>
                                        <Spinner className="animate-spin mr-2" size={16} />
                                        Importieren...
                                    </>
                                ) : (
                                    <>
                                        <Upload size={16} className="mr-2" />
                                        Importieren
                                    </>
                                )}
                            </Button>
                            <Button 
                                variant="outline"
                                onClick={handleAnalyze} 
                                disabled={!file || uploading || analyzing}
                                className="rounded-sm"
                                data-testid="analyze-btn"
                            >
                                {analyzing ? (
                                    <>
                                        <Spinner className="animate-spin mr-2" size={16} />
                                        Analysieren...
                                    </>
                                ) : (
                                    <>
                                        <TreeStructure size={16} className="mr-2" />
                                        XML analysieren
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Result Card */}
                <Card className="border" data-testid="result-card">
                    <CardHeader>
                        <CardTitle style={{ fontFamily: 'var(--font-cabinet)' }}>
                            Import-Ergebnis
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {result ? (
                            result.error ? (
                                <Alert variant="destructive" data-testid="import-error">
                                    <Warning size={16} />
                                    <AlertTitle>Import fehlgeschlagen</AlertTitle>
                                    <AlertDescription>{result.error}</AlertDescription>
                                </Alert>
                            ) : (
                                <div className="space-y-4">
                                    <Alert className="border-green-200 bg-green-50" data-testid="import-success">
                                        <Check size={16} className="text-green-600" />
                                        <AlertTitle className="text-green-800">Import erfolgreich</AlertTitle>
                                        <AlertDescription className="text-green-700">
                                            {result.created} neu angelegt, {result.updated} aktualisiert von {result.total} Immobilie(n).
                                        </AlertDescription>
                                    </Alert>

                                    <div className="grid grid-cols-3 gap-4">
                                        <Card className="border">
                                            <CardContent className="pt-4 text-center">
                                                <House size={24} className="mx-auto text-green-600 mb-2" />
                                                <p className="text-2xl font-bold" data-testid="import-created-count">{result.created}</p>
                                                <p className="text-sm text-muted-foreground">Neu angelegt</p>
                                            </CardContent>
                                        </Card>
                                        <Card className="border">
                                            <CardContent className="pt-4 text-center">
                                                <House size={24} className="mx-auto text-blue-600 mb-2" />
                                                <p className="text-2xl font-bold" data-testid="import-updated-count">{result.updated}</p>
                                                <p className="text-sm text-muted-foreground">Aktualisiert</p>
                                            </CardContent>
                                        </Card>
                                        <Card className="border">
                                            <CardContent className="pt-4 text-center">
                                                <FileZip size={24} className="mx-auto text-muted-foreground mb-2" />
                                                <p className="text-2xl font-bold" data-testid="import-total-count">{result.total}</p>
                                                <p className="text-sm text-muted-foreground">Gesamt</p>
                                            </CardContent>
                                        </Card>
                                    </div>

                                    {result.errors && result.errors.length > 0 && (
                                        <Alert variant="destructive">
                                            <Warning size={16} />
                                            <AlertTitle>Fehler bei einigen Einträgen</AlertTitle>
                                            <AlertDescription>
                                                <ul className="list-disc list-inside mt-2 text-sm">
                                                    {result.errors.slice(0, 5).map((err, idx) => (
                                                        <li key={idx}>{err}</li>
                                                    ))}
                                                    {result.errors.length > 5 && (
                                                        <li>... und {result.errors.length - 5} weitere Fehler</li>
                                                    )}
                                                </ul>
                                            </AlertDescription>
                                        </Alert>
                                    )}
                                </div>
                            )
                        ) : (
                            <div className="text-center py-12 text-muted-foreground">
                                <FileZip size={48} className="mx-auto mb-4 opacity-50" />
                                <p>Laden Sie eine ZIP-Datei hoch, um das Ergebnis zu sehen</p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* FTP File Analysis */}
            <Card className="border" data-testid="ftp-analyze-card">
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <CloudArrowDown size={22} className="text-muted-foreground" />
                            <div>
                                <CardTitle style={{ fontFamily: 'var(--font-cabinet)' }}>
                                    Datei vom FTP analysieren
                                </CardTitle>
                                <CardDescription>
                                    Eine Datei direkt vom FTP-Server laden und die XML-Struktur prüfen
                                </CardDescription>
                            </div>
                        </div>
                        <Button
                            variant="outline"
                            onClick={handleLoadFtpFiles}
                            disabled={ftpLoading}
                            className="rounded-sm"
                            data-testid="load-ftp-files-btn"
                        >
                            {ftpLoading ? (
                                <Spinner className="animate-spin mr-2" size={16} />
                            ) : (
                                <CloudArrowDown size={16} className="mr-2" />
                            )}
                            Dateien vom FTP laden
                        </Button>
                    </div>
                </CardHeader>
                {ftpFiles && (
                    <CardContent>
                        {ftpFiles.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-4">Keine ZIP-Dateien gefunden</p>
                        ) : (
                            <div className="space-y-1 max-h-[300px] overflow-y-auto">
                                {ftpFiles.map((f, idx) => (
                                    <div key={idx} className="flex items-center justify-between py-2 px-3 hover:bg-muted/50 rounded-sm text-sm border-b last:border-0">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <FileZip size={16} className="text-muted-foreground shrink-0" />
                                            <span className="truncate font-mono text-xs">{f.name}</span>
                                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0">
                                                {f.folder === 'processed' ? 'verarbeitet' : 'neu'}
                                            </Badge>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="rounded-sm shrink-0 text-xs"
                                            onClick={() => handleAnalyzeFtpFile(f.name, f.folder)}
                                            disabled={ftpAnalyzing}
                                            data-testid={`analyze-ftp-file-${idx}`}
                                        >
                                            {ftpAnalyzing ? <Spinner className="animate-spin" size={14} /> : <TreeStructure size={14} className="mr-1" />}
                                            Analysieren
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                )}
            </Card>

            {/* XML Analysis Result - Multi-file viewer */}
            {loadedAnalyses.length > 0 && (
                <Card className="border" data-testid="analysis-card">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <TreeStructure size={22} className="text-primary" />
                                <div>
                                    <CardTitle style={{ fontFamily: 'var(--font-cabinet)' }}>
                                        XML-Analyse
                                    </CardTitle>
                                    <CardDescription>
                                        {loadedAnalyses.length} Datei(en) geladen — zwischen Dateien umschalten
                                    </CardDescription>
                                </div>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => { setLoadedAnalyses([]); setAnalysisResult(null); }} className="text-xs">
                                Alle schließen
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* File Tabs */}
                        <div className="flex flex-wrap gap-2 pb-3 border-b">
                            {loadedAnalyses.map((analysis, idx) => (
                                <Button
                                    key={idx}
                                    variant={activeAnalysisIdx === idx ? 'default' : 'outline'}
                                    size="sm"
                                    className="rounded-sm text-xs font-mono"
                                    onClick={() => setActiveAnalysisIdx(idx)}
                                    data-testid={`analysis-tab-${idx}`}
                                >
                                    <FileZip size={12} className="mr-1" />
                                    {analysis.filename.length > 30 ? analysis.filename.slice(0, 27) + '...' : analysis.filename}
                                    <span className="ml-1.5 text-[10px] opacity-70">({analysis.properties.length})</span>
                                </Button>
                            ))}
                        </div>

                        {/* Active Analysis Content */}
                        {loadedAnalyses[activeAnalysisIdx] && (
                            <div className="space-y-4">
                                <div className="text-sm text-muted-foreground">
                                    {loadedAnalyses[activeAnalysisIdx].properties.length} Immobilie(n) in <span className="font-mono">{loadedAnalyses[activeAnalysisIdx].filename}</span>
                                </div>
                                {loadedAnalyses[activeAnalysisIdx].properties.map((prop, propIdx) => (
                                    <div key={propIdx} className="border rounded-sm overflow-hidden">
                                        <div className="bg-muted/50 px-4 py-2 font-medium text-sm border-b">
                                            Immobilie {propIdx + 1}
                                            {prop.freitexte?.objekttitel && (
                                                <span className="ml-2 text-muted-foreground font-normal">
                                                    — {typeof prop.freitexte.objekttitel === 'string' ? prop.freitexte.objekttitel : prop.freitexte.objekttitel._value || ''}
                                                </span>
                                            )}
                                        </div>
                                        <div className="p-4 max-h-[600px] overflow-y-auto">
                                            <XmlTreeView data={prop} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Single file analysis (from local upload) */}
            {analysisResult && loadedAnalyses.length === 0 && (
                <Card className="border" data-testid="analysis-card-single">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <TreeStructure size={22} className="text-primary" />
                                <div>
                                    <CardTitle style={{ fontFamily: 'var(--font-cabinet)' }}>
                                        XML-Analyse: {analysisResult.filename}
                                    </CardTitle>
                                    <CardDescription>
                                        {analysisResult.properties.length} Immobilie(n) gefunden · Alle Felder der XML-Datei
                                    </CardDescription>
                                </div>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => setAnalysisResult(null)} className="text-xs">
                                Schließen
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-6">
                            {analysisResult.properties.map((prop, propIdx) => (
                                <div key={propIdx} className="border rounded-sm overflow-hidden">
                                    <div className="bg-muted/50 px-4 py-2 font-medium text-sm border-b">
                                        Immobilie {propIdx + 1}
                                        {prop.freitexte?.objekttitel && (
                                            <span className="ml-2 text-muted-foreground font-normal">
                                                — {typeof prop.freitexte.objekttitel === 'string' ? prop.freitexte.objekttitel : prop.freitexte.objekttitel._value || ''}
                                            </span>
                                        )}
                                    </div>
                                    <div className="p-4 max-h-[600px] overflow-y-auto">
                                        <XmlTreeView data={prop} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Info Card */}
            <Card className="border" data-testid="info-card">
                <CardHeader>
                    <CardTitle style={{ fontFamily: 'var(--font-cabinet)' }}>
                        Über OpenImmo Import
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                            <h4 className="font-semibold mb-2">Unterstützte Formate</h4>
                            <ul className="text-sm text-muted-foreground space-y-1">
                                <li>• OpenImmo 1.2.7</li>
                                <li>• ZIP-Dateien mit XML und Bildern</li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-2">Immobilientypen</h4>
                            <ul className="text-sm text-muted-foreground space-y-1">
                                <li>• Wohnungen & Häuser</li>
                                <li>• Grundstücke</li>
                                <li>• Gewerbeimmobilien</li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-2">Importierte Daten</h4>
                            <ul className="text-sm text-muted-foreground space-y-1">
                                <li>• Titel, Beschreibung, Preise</li>
                                <li>• Flächen, Zimmer, Adressen</li>
                                <li>• Bilder & Ausstattung</li>
                            </ul>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Import-Protokoll */}
            <Card className="border" data-testid="import-log-card">
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <ClockCounterClockwise size={22} className="text-muted-foreground" />
                            <div>
                                <CardTitle style={{ fontFamily: 'var(--font-cabinet)' }}>
                                    Import-Protokoll
                                </CardTitle>
                                <CardDescription>Verlauf aller durchgeführten Importe</CardDescription>
                            </div>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            className="rounded-sm"
                            onClick={fetchLogs}
                            data-testid="refresh-logs-btn"
                        >
                            <ArrowsClockwise size={14} className="mr-1" />
                            Aktualisieren
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {logsLoading ? (
                        <div className="flex items-center justify-center py-8 text-muted-foreground">
                            <Spinner className="animate-spin mr-2" size={20} />
                            Lade Protokoll...
                        </div>
                    ) : importLogs.length === 0 ? (
                        <div className="text-center py-10 text-muted-foreground" data-testid="no-logs-message">
                            <ClockCounterClockwise size={40} className="mx-auto mb-3 opacity-40" />
                            <p>Noch keine Importe durchgeführt</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto" data-testid="import-log-table">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b text-left text-muted-foreground">
                                        <th className="pb-3 pr-4 font-medium">Zeitpunkt</th>
                                        <th className="pb-3 pr-4 font-medium">Dateiname</th>
                                        <th className="pb-3 pr-4 font-medium">Größe</th>
                                        <th className="pb-3 pr-4 font-medium">Benutzer</th>
                                        <th className="pb-3 pr-4 font-medium text-center">Neu</th>
                                        <th className="pb-3 pr-4 font-medium text-center">Aktualisiert</th>
                                        <th className="pb-3 pr-4 font-medium text-center">Gesamt</th>
                                        <th className="pb-3 font-medium">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {importLogs.map((log, idx) => (
                                        <tr key={idx} className="border-b last:border-0 hover:bg-muted/30 transition-colors" data-testid={`import-log-row-${idx}`}>
                                            <td className="py-3 pr-4 whitespace-nowrap">{formatTimestamp(log.timestamp)}</td>
                                            <td className="py-3 pr-4 font-medium max-w-[200px] truncate" title={log.filename}>
                                                {log.filename}
                                            </td>
                                            <td className="py-3 pr-4 whitespace-nowrap text-muted-foreground">
                                                {formatFileSize(log.file_size || 0)}
                                            </td>
                                            <td className="py-3 pr-4 text-muted-foreground">{log.user_email}</td>
                                            <td className="py-3 pr-4 text-center">
                                                <span className="font-semibold text-green-600">{log.created}</span>
                                            </td>
                                            <td className="py-3 pr-4 text-center">
                                                <span className="font-semibold text-blue-600">{log.updated}</span>
                                            </td>
                                            <td className="py-3 pr-4 text-center font-semibold">{log.total}</td>
                                            <td className="py-3">
                                                {log.status === 'success' ? (
                                                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200" data-testid={`log-status-${idx}`}>
                                                        Erfolgreich
                                                    </Badge>
                                                ) : log.status === 'error' ? (
                                                    <Badge variant="destructive" data-testid={`log-status-${idx}`}>
                                                        Fehler
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200" data-testid={`log-status-${idx}`}>
                                                        Teilweise
                                                    </Badge>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

